/**
 * The API tab's request (design "API tab"): the bar — method, service, path, "Sign in as", Send (⌘↵) — and the
 * request's parts: Params (the path's own and the query), Headers, Body (JSON, "Fill from schema" when the endpoint has
 * one, and whether the body matches it). With "A token or header you give", the header's name and value, kept in this
 * browser only. A viewer sends GET only: another method shows why Send is off.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { PaperPlaneRight, Plus, Trash } from '@phosphor-icons/react';
import ServiceStartButton from '../sandbox/ServiceStartButton';
import { METHODS, bodyHint, exampleFor, isReadMethod, methodClass, pathParamNames, userLabel } from '../../utils/apiTesterModel';

export const VIEWER_SEND = 'Viewers can send GET requests only';

function Rows({ rows, onChange, label }) {
  const set = (index, patch) => onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <div className="api-rows">
      {rows.map((row, index) => (
        <div className="api-row" key={index}>
          <input aria-label={`Use ${label} ${index + 1}`} checked={row.on !== false} onChange={(event) => set(index, { on: event.target.checked })} type="checkbox" />
          <input aria-label={`${label} name ${index + 1}`} className="data-input data-mono" onChange={(event) => set(index, { name: event.target.value })} placeholder="name" value={row.name} />
          <input aria-label={`${label} value ${index + 1}`} className="data-input data-mono api-row-value" onChange={(event) => set(index, { value: event.target.value })} placeholder="value" value={row.value} />
          <button aria-label={`Remove ${label} ${index + 1}`} className="data-btn is-icon is-ghost" onClick={() => onChange(rows.filter((_, i) => i !== index))} type="button"><Trash aria-hidden size={14} /></button>
        </div>
      ))}
      <button className="data-btn is-ghost" onClick={() => onChange([...rows, { name: '', value: '', on: true }])} type="button"><Plus aria-hidden size={14} />Add {label.toLowerCase()}</button>
    </div>
  );
}

Rows.propTypes = { label: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, rows: PropTypes.arrayOf(PropTypes.object).isRequired };

export function RequestBar({
  request, services, onChange, asValue, onAsChange, users, sending, onSend, canWrite, token, onTokenChange, projectId = null, onStarted = undefined,
}) {
  const blocked = !canWrite && !isReadMethod(request.method);
  const service = services.find((s) => s.service === request.service);
  return (
    <div className="api-card api-bar" data-testid="api-bar">
      <label className="data-sr" htmlFor="api-method">Method</label>
      <select className={`data-input data-mono api-method-select ${methodClass(request.method)}`} id="api-method" onChange={(event) => onChange({ method: event.target.value })} value={request.method}>
        {METHODS.map((method) => <option key={method} value={method}>{method}</option>)}
      </select>
      <label className="data-sr" htmlFor="api-service">Service</label>
      <select className="data-input data-mono api-service-select" id="api-service" onChange={(event) => onChange({ service: event.target.value })} value={request.service}>
        {services.map((s) => <option key={s.service} value={s.service}>{s.name}</option>)}
      </select>
      <label className="data-sr" htmlFor="api-path">Path</label>
      <input className="data-input data-mono api-path" id="api-path" onChange={(event) => onChange({ path: event.target.value })} spellCheck={false} value={request.path} />
      <label className="data-label" htmlFor="api-as">Sign in as</label>
      <select className="data-input api-as" id="api-as" onChange={(event) => onAsChange(event.target.value)} value={asValue}>
        <option value="visitor">A visitor (no app sign-in)</option>
        {users.map((user) => <option disabled={user.disabled} key={user.id} value={`user:${user.id}`}>{userLabel(user)}</option>)}
        <option value="token">A token or header you give</option>
        <option value="manage">Users and roles…</option>
      </select>
      <button
        className="data-btn is-primary"
        data-testid="api-send"
        disabled={sending || blocked || !request.path}
        onClick={onSend}
        title={blocked ? VIEWER_SEND : 'Send (⌘↵)'}
        type="button"
      >
        <PaperPlaneRight aria-hidden size={15} />
        {sending ? 'Sending…' : 'Send'}
        <span className="data-kbd">⌘↵</span>
      </button>
      {blocked && <p className="api-bar-note data-full">{VIEWER_SEND}: ask an editor of the project to try this one.</p>}
      {service && !service.running && (
        <p className="api-bar-note data-full api-group-stopped">
          <span>{service.name} is not running: start its preview first.</span>
          {canWrite && projectId && service.state !== 'building' && <ServiceStartButton onStarted={onStarted} projectId={projectId} serviceName={service.service} />}
        </p>
      )}
      {asValue === 'token' && (
        <div className="api-token data-full" data-testid="api-token">
          <label className="data-sr" htmlFor="api-token-name">Header</label>
          <input className="data-input data-mono" id="api-token-name" onChange={(event) => onTokenChange({ ...token, name: event.target.value })} placeholder="Authorization" value={token.name} />
          <label className="data-sr" htmlFor="api-token-value">Value</label>
          <input autoComplete="off" className="data-input data-mono api-row-value" id="api-token-value" onChange={(event) => onTokenChange({ ...token, value: event.target.value })} placeholder="Bearer …" type="password" value={token.value} />
          <span className="data-muted">Kept in this browser only, masked in History and in &quot;Copy as curl&quot;.</span>
        </div>
      )}
    </div>
  );
}

RequestBar.propTypes = {
  onStarted: PropTypes.func,
  projectId: PropTypes.string,
  asValue: PropTypes.string.isRequired,
  canWrite: PropTypes.bool,
  onAsChange: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onTokenChange: PropTypes.func.isRequired,
  request: PropTypes.object.isRequired,
  sending: PropTypes.bool,
  services: PropTypes.arrayOf(PropTypes.object).isRequired,
  token: PropTypes.shape({ name: PropTypes.string, value: PropTypes.string }).isRequired,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export function RequestParts({ request, endpoint, onChange, addedNote }) {
  const hasBody = !isReadMethod(request.method);
  const [part, setPart] = useState(hasBody && endpoint?.body ? 'body' : 'params');
  const shown = part === 'body' && !hasBody ? 'params' : part;
  const names = pathParamNames(request.path);
  const schema = endpoint?.body?.schema || null;
  const hint = bodyHint(request.body, schema, endpoint?.body?.schemaName || null);
  const headerCount = request.headers.filter((h) => h.on !== false && h.name).length;
  const queryCount = request.query.filter((q) => q.on !== false && q.name).length + names.length;
  const parts = [
    { id: 'params', label: `Params${queryCount ? ` ${queryCount}` : ''}` },
    { id: 'headers', label: `Headers${headerCount ? ` ${headerCount}` : ''}` },
    ...(hasBody ? [{ id: 'body', label: 'Body' }] : []),
  ];
  return (
    <section aria-label="Request" className="api-card api-half">
      <div aria-label="Request parts" className="api-sub" role="tablist">
        {parts.map((p) => (
          <button aria-selected={shown === p.id} className={shown === p.id ? 'is-on' : ''} key={p.id} onClick={() => setPart(p.id)} role="tab" type="button">{p.label}</button>
        ))}
      </div>
      <div className="api-part" role="tabpanel">
        {shown === 'params' && (
          <>
            {names.length > 0 && (
              <div className="api-rows">
                <span className="data-label">Path</span>
                {names.map((name) => (
                  <div className="api-row" key={name}>
                    <label className="data-mono api-param-name" htmlFor={`api-param-${name}`}>{name}</label>
                    <input className="data-input data-mono api-row-value" id={`api-param-${name}`} onChange={(event) => onChange({ params: { ...request.params, [name]: event.target.value } })} placeholder="required" value={request.params[name] || ''} />
                  </div>
                ))}
              </div>
            )}
            <span className="data-label">Query</span>
            <Rows label="Query parameter" onChange={(query) => onChange({ query })} rows={request.query} />
          </>
        )}
        {shown === 'headers' && <Rows label="Header" onChange={(headers) => onChange({ headers })} rows={request.headers} />}
        {shown === 'body' && (
          <>
            <div className="data-row-gap is-wrap">
              <span className="data-pill">JSON</span>
              {hint.text && <span className={`api-hint is-${hint.tone}`} data-testid="api-body-hint">{hint.text}</span>}
              <span className="data-grow" />
              {schema && <button className="data-btn" onClick={() => onChange({ body: JSON.stringify(exampleFor(schema) ?? {}, null, 2) })} type="button">Fill from schema</button>}
            </div>
            <label className="data-sr" htmlFor="api-body">Body</label>
            <textarea className="data-textarea data-mono api-body" id="api-body" onChange={(event) => onChange({ body: event.target.value })} rows={10} spellCheck={false} value={request.body} />
          </>
        )}
        <p className="data-footnote">{addedNote} Your Flow sign-in is never sent to the app.</p>
      </div>
    </section>
  );
}

RequestParts.propTypes = {
  addedNote: PropTypes.string,
  endpoint: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  request: PropTypes.object.isRequired,
};
